import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { startGoogleLogin } from "../lib/googleAuth";
import { Sparkles, X } from "lucide-react";

const PENDING_KEY = "gully_pending_continuity_session";
const SHOWN_KEY = "gully_continuity_shown_session";

/**
 * Iter 44.1 — Continuity Capture Before Paywall.
 *
 * Non-blocking card shown in the Chat scroll for ANONYMOUS users who have
 * hit a high-value moment (≥3 successful reviews, OR 1 review + meaningful
 * follow-up conversation). Single offer: save your cricket history with
 * Google. Never blocks the chat or uploads. 7-day cooldown on dismissal
 * (server-enforced).
 *
 * No pricing language. No paywall language. No upgrade copy.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH
 */
export default function ContinuityPrompt({ sessionId, eligible, onDismissed }) {
  const [hidden, setHidden] = useState(false);
  const shownTrackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId || !eligible || hidden) return;
    // Track `shown` exactly once per session per mount.
    if (shownTrackedRef.current) return;
    shownTrackedRef.current = true;
    api.post("/web/continuity/event", {
      session_id: sessionId, event: "shown",
    }).catch(() => {});
    try { sessionStorage.setItem(SHOWN_KEY, sessionId); } catch { /* noop */ }
  }, [sessionId, eligible, hidden]);

  if (!sessionId || !eligible || hidden) return null;

  async function onGoogleClick() {
    try {
      await api.post("/web/continuity/event", {
        session_id: sessionId, event: "google_clicked",
      });
    } catch { /* non-blocking */ }
    try { localStorage.setItem(PENDING_KEY, sessionId); } catch { /* noop */ }
    // P0 2026-10-01 — Gully-owned Google OAuth (no Emergent). Uses the
    // shared helper which fetches the auth URL from Gully's backend.
    await startGoogleLogin("/chat");
  }

  async function onDismiss() {
    setHidden(true);
    try {
      await api.post("/web/continuity/event", {
        session_id: sessionId, event: "dismissed",
      });
    } catch { /* non-blocking */ }
    if (onDismissed) onDismissed();
  }

  return (
    <div
      className="rounded-2xl border border-emerald-900/40 bg-gradient-to-br from-emerald-950/30 to-neutral-900/40 p-4 shadow-lg"
      data-testid="continuity-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="flex-none mt-0.5">
          <Sparkles size={18} className="text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-medium text-emerald-100 mb-1"
            data-testid="continuity-prompt-title"
          >
            Want Gully to remember your cricket?
          </div>
          <p className="text-xs text-neutral-300 mb-3 leading-relaxed">
            Save your reviews and the patterns Gully has spotted so they
            stick around across devices. One tap with Google — no signup form.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onGoogleClick}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium rounded-full bg-emerald-500 text-neutral-950 hover:bg-emerald-400 transition-colors"
              data-testid="continuity-prompt-google-btn"
            >
              <GoogleMark />
              Continue with Google
            </button>
            <button
              onClick={onDismiss}
              className="text-xs text-neutral-400 hover:text-neutral-200 px-2 py-1.5"
              data-testid="continuity-prompt-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex-none text-neutral-500 hover:text-neutral-300 -mr-1 -mt-1"
          data-testid="continuity-prompt-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.8h5.3c-.2 1.3-1.6 3.8-5.3 3.8-3.2 0-5.8-2.6-5.8-5.8s2.6-5.8 5.8-5.8c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.6 3.7 14.5 2.8 12 2.8 6.9 2.8 2.8 6.9 2.8 12s4.1 9.2 9.2 9.2c5.3 0 8.8-3.7 8.8-9 0-.6-.1-1.1-.2-1.6H12z"/>
    </svg>
  );
}

ContinuityPrompt.STORAGE = { PENDING_KEY, SHOWN_KEY };
